import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Send, Archive, CheckCircle2, Sparkles, Eye, RotateCcw } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { Pill, CHANNEL_LABEL, CATEGORY_LABEL } from "./shared";
import { Wordmark } from "@/components/Brand";

const SAMPLE_VARS = {
    customer_first_name: "Sarah",
    customer_last_name: "Miller",
    customer_full_name: "Sarah Miller",
    service_name: "Consultation - Hair filler",
    appointment_date: "12 March 2026",
    appointment_time: "10:45",
    appointment_datetime: "12 March 2026, 10:45",
    practitioner_name: "Angela",
    clinic_name: "Pout Aesthetics",
    clinic_phone: "0000 000 0000",
    clinic_address: "Pout Aesthetics Studio",
    deposit_amount: "50.00",
    deposit_currency: "GBP",
    outstanding_amount: "150.00",
    booking_reference: "POUT-0421",
    forms_link: "https://pout.example/f/token",
    reschedule_link: "https://pout.example/reschedule/token",
    cancel_link: "https://pout.example/cancel/token",
    review_link: "https://pout.example/review/token",
    rebooking_link: "https://pout.example/rebook/token",
    aftercare_link: "https://pout.example/aftercare/token",
    unsubscribe_link: "https://pout.example/unsubscribe/token",
    manage_preferences_link: "https://pout.example/prefs/token",
};

export function CommsTemplateBuilder() {
    const { id } = useParams();
    const nav = useNavigate();
    const [tpl, setTpl] = useState(null);
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [form, setForm] = useState({ name: "", channel: "email", category: "transactional", subject: "", body: "", description: "" });
    const [preview, setPreview] = useState(null);
    const bodyRef = useRef(null);
    const subjectRef = useRef(null);
    const [lastFocused, setLastFocused] = useState("body");

    const load = () => {
        setError(null);
        Promise.all([staffApi.commsGetTemplate(id), staffApi.commsMeta()])
            .then(([tr, mr]) => {
                setTpl(tr.template);
                setMeta(mr);
                setForm({
                    name: tr.template.name || "",
                    channel: tr.template.channel,
                    category: tr.template.category,
                    subject: tr.template.subject || "",
                    body: tr.template.body || "",
                    description: tr.template.description || "",
                });
                setDirty(false);
            })
            .catch(setError);
    };
    useEffect(load, [id]);

    const usedVars = useMemo(() => extractVars(form.subject + " " + form.body), [form.subject, form.body]);
    const unknownVars = useMemo(() => {
        const allowed = new Set(meta?.allowed_variables || Object.keys(SAMPLE_VARS));
        return usedVars.filter((v) => !allowed.has(v));
    }, [usedVars, meta]);

    const doSave = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await staffApi.commsPatchTemplate(id, form);
            const tr = await staffApi.commsGetTemplate(id);
            setTpl(tr.template);
            setForm({
                name: tr.template.name || "",
                channel: tr.template.channel,
                category: tr.template.category,
                subject: tr.template.subject || "",
                body: tr.template.body || "",
                description: tr.template.description || "",
            });
            setDirty(false);
            toast.success("Saved");
        } catch (e) { toast.error(e.message || "Save failed"); }
        finally { setBusy(false); }
    };

    const doPublish = async () => {
        if (busy) return;
        if (unknownVars.length) { toast.error(`Unknown variables: ${unknownVars.join(", ")}`); return; }
        setBusy(true);
        try {
            if (dirty) {
                await staffApi.commsPatchTemplate(id, form);
            }
            await staffApi.commsTemplateStatus(id, "published");
            toast.success("Published");
            const tr = await staffApi.commsGetTemplate(id);
            setTpl(tr.template);
            setForm({
                name: tr.template.name || "",
                channel: tr.template.channel,
                category: tr.template.category,
                subject: tr.template.subject || "",
                body: tr.template.body || "",
                description: tr.template.description || "",
            });
            setDirty(false);
        } catch (e) { toast.error(e.message || "Publish failed"); }
        finally { setBusy(false); }
    };

    const doArchive = async () => {
        if (!window.confirm("Archive this template? Existing queue records will remain intact but no new rules can use it.")) return;
        setBusy(true);
        try { await staffApi.commsDeleteTemplate(id); toast.success("Archived"); load(); }
        catch (e) { toast.error(e.message || "Archive failed"); }
        finally { setBusy(false); }
    };

    const doPreview = async () => {
        setBusy(true);
        try {
            const r = await staffApi.commsPreview({ subject: form.subject, body: form.body, variables: SAMPLE_VARS });
            setPreview(r);
        } catch (e) { toast.error(e.message || "Preview failed"); }
        finally { setBusy(false); }
    };
    // Auto-preview
    useEffect(() => {
        const t = setTimeout(() => {
            if (form.body || form.subject) doPreview();
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [form.subject, form.body]);

    const setField = (k, v) => { setForm({ ...form, [k]: v }); setDirty(true); };

    const insertVariable = (v) => {
        const token = `{{${v}}}`;
        const target = lastFocused === "subject" ? subjectRef.current : bodyRef.current;
        if (!target) {
            setField(lastFocused === "subject" ? "subject" : "body", (lastFocused === "subject" ? form.subject : form.body) + token);
            return;
        }
        const start = target.selectionStart ?? target.value.length;
        const end = target.selectionEnd ?? target.value.length;
        const before = target.value.slice(0, start);
        const after = target.value.slice(end);
        const next = before + token + after;
        setField(lastFocused === "subject" ? "subject" : "body", next);
        requestAnimationFrame(() => {
            target.focus();
            const pos = before.length + token.length;
            target.setSelectionRange(pos, pos);
        });
    };

    if (error) return <ErrorState error={error} onRetry={load} />;
    if (!tpl || !meta) return <Skeleton className="h-64" />;

    const readonly = tpl.status === "archived";
    const channels = meta.channels || ["email","sms","whatsapp"];
    const categories = meta.template_categories || Object.keys(CATEGORY_LABEL);

    return (
        <div className="space-y-5" data-testid="template-builder">
            <button onClick={() => nav("/staff/communications/templates")} className="text-xs text-white/50 hover:text-gold flex items-center gap-1" data-testid="tb-back">
                <ArrowLeft className="h-3 w-3" /> Back to templates
            </button>

            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85 flex items-center gap-2">
                        Template <Pill value={tpl.status} /> <span className="text-white/40 normal-case tracking-normal">v{tpl.version}</span>
                    </div>
                    <input value={form.name} onChange={(e) => setField("name", e.target.value)} disabled={readonly}
                           data-testid="tb-name"
                           className="mt-2 bg-transparent font-serif font-light text-4xl text-white leading-[1.05] outline-none border-b border-transparent focus:border-gold/50 w-full max-w-xl" />
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={doSave} disabled={busy || readonly} data-testid="tb-save"
                            className="h-10 rounded-full border hairline text-white px-4 text-sm inline-flex items-center gap-1 disabled:opacity-40">
                        <Save className="h-3.5 w-3.5" /> Save
                    </button>
                    <button onClick={doPublish} disabled={busy || readonly} data-testid="tb-publish"
                            className="h-10 rounded-full bg-gold text-navy px-5 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-40">
                        <Send className="h-3.5 w-3.5" /> {tpl.status === "published" ? "Republish" : "Publish"}
                    </button>
                    {tpl.status !== "archived" && (
                        <button onClick={doArchive} disabled={busy} data-testid="tb-archive"
                                className="h-10 rounded-full border hairline text-red-300 px-4 text-sm inline-flex items-center gap-1">
                            <Archive className="h-3.5 w-3.5" /> Archive
                        </button>
                    )}
                </div>
            </header>

            {readonly && (
                <div className="rounded-xl border hairline bg-red-500/5 p-3 text-xs text-red-200" data-testid="readonly-notice">
                    This template is archived. Duplicate it to create an editable copy.
                </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
                {/* Editor */}
                <div className="rounded-2xl glass p-5 space-y-4" data-testid="tb-editor">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Channel</div>
                            <select value={form.channel} onChange={(e) => setField("channel", e.target.value)} disabled={readonly}
                                    data-testid="tb-channel"
                                    className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                                {channels.map((c) => <option key={c} value={c} className="bg-navy">{CHANNEL_LABEL[c]}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Category</div>
                            <select value={form.category} onChange={(e) => setField("category", e.target.value)} disabled={readonly}
                                    data-testid="tb-category"
                                    className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                                {categories.map((c) => <option key={c} value={c} className="bg-navy">{CATEGORY_LABEL[c] || c}</option>)}
                            </select>
                        </label>
                    </div>

                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Internal description</div>
                        <input value={form.description} onChange={(e) => setField("description", e.target.value)} disabled={readonly}
                               data-testid="tb-description"
                               placeholder="Optional note for staff (not visible to customers)"
                               className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
                    </label>

                    {form.channel === "email" && (
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Subject line</div>
                            <input ref={subjectRef} value={form.subject} onChange={(e) => setField("subject", e.target.value)}
                                   onFocus={() => setLastFocused("subject")} disabled={readonly}
                                   data-testid="tb-subject"
                                   placeholder="e.g. Your appointment with {{practitioner_name}} is tomorrow"
                                   className="w-full bg-transparent border hairline rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-gold" />
                        </label>
                    )}

                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5 flex justify-between">
                            <span>Body</span>
                            <span className="text-white/40 normal-case tracking-normal">{form.body.length} chars</span>
                        </div>
                        <textarea ref={bodyRef} value={form.body} onChange={(e) => setField("body", e.target.value)}
                                  onFocus={() => setLastFocused("body")} disabled={readonly}
                                  data-testid="tb-body"
                                  rows={form.channel === "sms" ? 6 : 12}
                                  placeholder={form.channel === "sms"
                                      ? "Hi {{customer_first_name}}, this is a reminder about your {{service_name}} appointment on {{appointment_date}} at {{appointment_time}}."
                                      : "Dear {{customer_first_name}},\n\nThis is a friendly reminder about your upcoming {{service_name}} appointment with {{practitioner_name}} on {{appointment_date}} at {{appointment_time}}.\n\nWarmly,\n{{clinic_name}}"}
                                  className="w-full bg-transparent border hairline rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-gold font-mono" />
                    </label>

                    {unknownVars.length > 0 && (
                        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-[11px] text-red-200" data-testid="tb-unknown-vars">
                            Unknown variables: {unknownVars.map((v) => `{{${v}}}`).join(", ")} — remove these before publishing.
                        </div>
                    )}

                    {/* Variable palette */}
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-2 flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-gold" /> Insert variable
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-testid="tb-variables">
                            {(meta.allowed_variables || []).map((v) => (
                                <button key={v} onClick={() => insertVariable(v)} disabled={readonly}
                                        data-testid={`insert-var-${v}`}
                                        className={`text-[10px] font-mono px-2 h-6 rounded-full border transition-colors ${
                                            usedVars.includes(v)
                                                ? "border-gold/60 text-gold bg-gold/10"
                                                : "border-white/15 text-white/70 hover:border-gold/40 hover:text-gold"
                                        }`}>
                                    {`{{${v}}}`}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Preview */}
                <div className="rounded-2xl glass p-5 space-y-3" data-testid="tb-preview">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 flex items-center gap-2">
                                <Eye className="h-3 w-3 text-gold" /> Live preview
                            </div>
                            <p className="text-[11px] text-white/45 mt-1">Rendered with sample values. Nothing is sent.</p>
                        </div>
                        <button onClick={doPreview} disabled={busy} className="h-8 px-3 rounded-full border hairline text-[11px] text-white/70 hover:text-gold inline-flex items-center gap-1" data-testid="tb-refresh-preview">
                            <RotateCcw className="h-3 w-3" /> Refresh
                        </button>
                    </div>
                    <ChannelPreview channel={form.channel} preview={preview} />
                </div>
            </div>
        </div>
    );
}

function extractVars(text) {
    const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    const out = [];
    let m;
    while ((m = re.exec(text || "")) !== null) if (!out.includes(m[1])) out.push(m[1]);
    return out;
}

function ChannelPreview({ channel, preview }) {
    if (!preview) return <Skeleton className="h-40" />;
    if (channel === "email") {
        return (
            <div className="rounded-xl border hairline bg-[#0f1730] overflow-hidden" data-testid="preview-email">
                <div className="px-4 py-3 border-b hairline bg-white/[0.03]">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/40">Subject</div>
                    <div className="text-white text-sm mt-0.5 truncate">{preview.subject || <span className="text-white/40 italic">—</span>}</div>
                </div>
                <div className="p-6 space-y-3 max-h-[500px] overflow-y-auto">
                    <Wordmark small />
                    <div className="text-white/85 text-sm whitespace-pre-wrap leading-relaxed font-ui">
                        {preview.body || <span className="text-white/40 italic">Empty body</span>}
                    </div>
                    <div className="text-[10px] text-white/30 pt-4 border-t hairline">Pout Aesthetics · {new Date().getFullYear()}</div>
                </div>
            </div>
        );
    }
    if (channel === "sms") {
        return (
            <div className="rounded-xl border hairline p-4 bg-[#0f1730]" data-testid="preview-sms">
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-2">SMS · {preview.body.length}/160 chars</div>
                <div className="max-w-xs bg-[#1a2545] rounded-2xl rounded-bl-none px-4 py-2 text-white text-sm whitespace-pre-wrap">
                    {preview.body || <span className="text-white/40 italic">Empty body</span>}
                </div>
                {preview.body.length > 160 && (
                    <div className="mt-2 text-[10px] text-amber-300">Warning: this exceeds a single SMS segment.</div>
                )}
            </div>
        );
    }
    // whatsapp
    return (
        <div className="rounded-xl border hairline p-4 bg-[#0b1e11]" data-testid="preview-whatsapp">
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-2">WhatsApp preview</div>
            <div className="max-w-sm bg-[#005c4b] rounded-2xl rounded-tr-none px-3 py-2 text-white text-sm whitespace-pre-wrap">
                {preview.body || <span className="text-white/40 italic">Empty body</span>}
            </div>
        </div>
    );
}
